import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Label,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import styles from "./PatientOverview.module.css";
import Skeleton from "../Skeleton";

const DeviceDistributionChart = ({
  data = [],
  totalDevices = 0,
  title = "Device Distribution",
  isLoading = false,
}) => {
  const totalAssigned = data.reduce(
    (sum, item) => sum + (item?.value ?? 0),
    0
  );

  const showLoading = isLoading;
  const showEmptyState = totalAssigned <= 0;

  // ✅ Filter zero-value slices so padding works properly
  const filteredData = useMemo(
    () => data.filter((item) => item?.value > 0),
    [data]
  );

  const renderDeviceTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const row = payload[0]?.payload || {};
    const pct = row?.pct ?? 0;
    const rounded = Math.round(pct);

    return (
      <div className={styles.tooltipBox}>
        <div className={styles.tooltipTitle}>{row.name}</div>
        <div className={styles.tooltipRow}>
          <span className={styles.tooltipLabel}>Count</span>
          <span className={styles.tooltipValue}>{row.value ?? 0}</span>
        </div>
        <div className={styles.tooltipRow}>
          <span className={styles.tooltipLabel}>Percent</span>
          <span className={styles.tooltipValue}>
            {rounded > 0 ? `${rounded}%` : "0%"}
          </span>
        </div>
      </div>
    );
  };

  // ✅ Label Line (hide if 0%)
  const renderLabelLine = ({ cx, cy, midAngle, outerRadius, payload }) => {
    const pct = payload?.pct ?? 0;
    const rounded = Math.round(pct);
    if (rounded <= 0) return null;

    const RADIAN = Math.PI / 180;

    const sx = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const sy = cy + outerRadius * Math.sin(-midAngle * RADIAN);
    const ex = cx + (outerRadius + 14) * Math.cos(-midAngle * RADIAN);
    const ey = cy + (outerRadius + 14) * Math.sin(-midAngle * RADIAN);

    return (
      <line
        x1={sx}
        y1={sy}
        x2={ex}
        y2={ey}
        stroke={payload?.color || "#9AA0B4"}
        strokeWidth={1}
        strokeLinecap="round"
      />
    );
  };

  // ✅ Outside Label (hide if 0%)
  const renderOutsideLabel = ({
    value,
    cx,
    cy,
    midAngle,
    outerRadius,
    payload,
  }) => {
    if (!totalDevices || value <= 0) return null;

    const pct = payload?.pct ?? 0;
    const rounded = Math.round(pct);
    if (rounded <= 0) return null;

    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 16;

    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const textAnchor = x > cx ? "start" : "end";
    const color = payload?.color || "#333";

    return (
      <text
        x={x}
        y={y}
        fill={color}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {rounded}%
      </text>
    );
  };

  return (
    <div className={styles.chartCard}>
      <h3 className={styles.chartTitle}>{title}</h3>
      <div className={styles.deviceStatusBody}>
        <div className={styles.pieContainer}>
          {showLoading ? (
            <div className={styles.emptyState}>
              <div
                className={`${styles.emptyRing} ${styles.loadingRing}`}
              >
                <span className={styles.emptyCenterValue}>...</span>
              </div>
              <div className={styles.emptyText}>Loading...</div>
            </div>
          ) : showEmptyState ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyRing}>
                <span className={styles.emptyCenterValue}>
                  {totalDevices}
                </span>
              </div>
              <div className={styles.emptyText}>
                No data available
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie
                  data={filteredData}   // ✅ use filtered data
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={94}
                  paddingAngle={
                    filteredData.length > 1 ? 4 : 0
                  }   
                  dataKey="value"
                  labelLine={renderLabelLine}
                  label={renderOutsideLabel}
                >
                  {filteredData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke="none"
                    />
                  ))}
                  <Label
                    value={totalDevices}
                    position="center"
                    className={styles.pieCenterLabel}
                  />
                </Pie>
                <Tooltip content={renderDeviceTooltip} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={styles.statusLegend}>
          {showLoading ? (
            <>
              <div className={`${styles.statusRow} ${styles.statusRowLoading}`}>
                <Skeleton width="55%" height="8px" radius="999px" />
              </div>
              <div className={`${styles.statusRow} ${styles.statusRowLoading}`}>
                <Skeleton width="70%" height="8px" radius="999px" />
              </div>
              <div className={`${styles.statusRow} ${styles.statusRowLoading}`}>
                <Skeleton width="45%" height="8px" radius="999px" />
              </div>
              <div className={`${styles.statusRow} ${styles.statusRowLoading}`}>
                <Skeleton width="65%" height="8px" radius="999px" />
              </div>
            </>
          ) : (
            <>
              <div className={styles.statusRow}>
                <div className={styles.statusInfo}>
                  <span
                    className={styles.statusName}
                    style={{ fontWeight: 600 }}
                  >
                    Total Devices
                  </span>
                </div>
                <span
                  className={styles.statusValue}
                  style={{ fontWeight: 600 }}
                >
                  {totalDevices}
                </span>
              </div>

              {data.map((item) => (
                <div key={item.name} className={styles.statusRow}>
                  <div className={styles.statusInfo}>
                    <span
                      className={styles.statusDot}
                      style={{ backgroundColor: item.color }}
                    />
                    <span className={styles.statusName}>
                      {item.name}
                    </span>
                  </div>
                  <span className={styles.statusValue}>
                    {item.value}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceDistributionChart;
